<template>
  <div class="smart-select-test-page space-y-6">
    <el-alert
      title="SmartSelect 场景验证"
      type="info"
      description="覆盖 options / v-model 的多种类型组合，以及初始化有值和无值 (null、undefined、''、[]) 的情况。"
      show-icon
      :closable="false"
    />

    <div class="flex flex-wrap gap-2">
      <el-button type="primary" size="mini" @click="resetAll">全部重置</el-button>
      <el-button size="mini" @click="expandOptions = !expandOptions">
        {{ expandOptions ? "折叠所有 Options" : "展开所有 Options" }}
      </el-button>
    </div>

    <section
      v-for="group in groups"
      :key="group.key"
      class="group-card"
    >
      <header class="group-card__header">
        <div>
          <h2 class="group-card__title">{{ group.title }}</h2>
          <p class="group-card__desc">{{ group.description }}</p>
          <p class="group-card__meta">
            Options: {{ group.optionsLabel }} · v-model 组合: {{ group.targets }}
          </p>
        </div>
        <span class="group-card__tag">{{ group.optionsKey }}</span>
      </header>

      <details :open="expandOptions" class="options-preview">
        <summary>查看 Options 数据</summary>
        <pre>{{ getOptionsPreview(group.optionsKey) }}</pre>
      </details>

      <div class="group-grid">
        <div
          v-for="state in group.states"
          :key="state.key"
          class="state-card"
        >
          <div class="state-card__header">
            <div>
              <p class="state-card__title">{{ state.label }}</p>
              <p class="state-card__meta">{{ state.meta }}</p>
            </div>
            <el-button type="text" size="mini" @click="resetValue(state.key)">
              重置
            </el-button>
          </div>

          <SmartSelect
            v-model="formValues[state.key]"
            :options="getOptions(state.optionsKey || group.optionsKey)"
            :multiple="state.multiple"
            :value-type="state.valueType"
            :display-field="state.displayField || group.displayField"
            :value-field="state.valueField || group.valueField"
            :auto-select="false"
            :placeholder="state.placeholder || '请选择'"
            class="w-full"
          />

          <pre class="value-preview">{{ formatValue(formValues[state.key]) }}</pre>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";

const optionsCatalog = {
  stringPrimitives: ["Device A", "Device B", "Device C"],
  numberPrimitives: [1001, 1002, 1003],
  booleanPrimitives: [true, false],
  statusOptionsUndefined: [
    { id: undefined, name: "All States (undefined)" },
    { id: 1, name: "Enabled" },
    { id: 0, name: "Disabled" },
  ],
  statusOptionsNull: [
    { id: null, name: "All States (null)" },
    { id: 1, name: "Enabled" },
    { id: 0, name: "Disabled" },
  ],
  stringObjects: [
    { id: "sensor-alpha", name: "Sensor Alpha" },
    { id: "sensor-beta", name: "Sensor Beta" },
    { id: "sensor-gamma", name: "Sensor Gamma" },
  ],
  numberObjects: [
    { id: 11, name: "Machine 11" },
    { id: 22, name: "Machine 22" },
    { id: 33, name: "Machine 33" },
  ],
  booleanObjects: [
    { id: true, name: "开启" },
    { id: false, name: "关闭" },
  ],
};

function stringifyOptions(value) {
  return JSON.stringify(
    value,
    (_, val) => (val === undefined ? "__undefined__" : val),
    2
  );
}

const scenarioGroups = [
  {
    key: "clearable-empty-values",
    title: "Clear & Empty States",
    description:
      "验证 clearable 行为在不同空值 (null / undefined / '' / []) 下的表现，确保 v-model 返回期望的空值。",
    optionsKey: "stringPrimitives",
    optionsLabel: "Array<string>",
    targets: "null · undefined · '' · []",
    states: [
      {
        key: "clearSingleNull",
        label: "单选清空 ⇒ null",
        valueType: "string",
        multiple: false,
        initial: null,
        placeholder: "清空时返回 null",
        meta: "v-model 空值: null",
      },
      {
        key: "clearSingleUndefined",
        label: "单选清空 ⇒ undefined",
        valueType: "string",
        multiple: false,
        initial: undefined,
        placeholder: "清空时返回 undefined",
        meta: "v-model 空值: undefined",
      },
      {
        key: "clearSingleEmptyString",
        label: "单选清空 ⇒ ''",
        valueType: "string",
        multiple: false,
        initial: "",
        placeholder: "清空时返回空字符串",
        meta: "v-model 空值: ''",
      },
      {
        key: "clearMultiEmptyArray",
        label: "多选清空 ⇒ []",
        valueType: "array<string>",
        multiple: true,
        initial: () => [],
        placeholder: "清空时返回 []",
        meta: "v-model 空值: []",
      },
    ],
  },
  {
    key: "all-state-variants",
    title: "All State Filters",
    description:
      "包含显式 All 选项，其值为 undefined 或 null，用于模拟传空 query 参数的交互。",
    optionsKey: "statusOptionsUndefined",
    optionsLabel: "[{id: undefined}|{id: null}, ...]",
    targets: "undefined · null",
    valueField: "id",
    displayField: "name",
    states: [
      {
        key: "allStateUndefined",
        label: "All State (undefined)",
        valueType: "string",
        multiple: false,
        initial: undefined,
        placeholder: "选择或清空触发 undefined",
        meta: "'All States (undefined)' ⇒ v-model undefined",
        optionsKey: "statusOptionsUndefined",
      },
      {
        key: "allStateNull",
        label: "All State (null)",
        valueType: "string",
        multiple: false,
        initial: null,
        placeholder: "All option 传 null",
        meta: "'All States (null)' ⇒ v-model null",
        optionsKey: "statusOptionsNull",
      },
    ],
  },
  {
    key: "primitive-strings",
    title: "Options = Array<string>",
    description: "验证 string / Array<string> 的 v-model，以及 null、undefined、'' 场景。",
    optionsKey: "stringPrimitives",
    optionsLabel: "Array<string>",
    targets: "string · Array<string>",
    states: [
      {
        key: "stringSingleWithValue",
        label: "单选 (string) 初始化有值",
        valueType: "string",
        multiple: false,
        initial: "Device B",
        meta: "v-model: string | init: 'Device B'",
      },
      {
        key: "stringSingleNull",
        label: "单选 (string) 初始化 null",
        valueType: "string",
        multiple: false,
        initial: null,
        meta: "v-model: string | init: null",
      },
      {
        key: "stringSingleUndefined",
        label: "单选 (string) 初始化 undefined",
        valueType: "string",
        multiple: false,
        initial: undefined,
        meta: "v-model: string | init: undefined",
      },
      {
        key: "stringSingleEmpty",
        label: "单选 (string) 初始化空字符串",
        valueType: "string",
        multiple: false,
        initial: "",
        meta: "v-model: string | init: ''",
      },
      {
        key: "stringArrayWithValue",
        label: "多选 (Array<string>) 有默认值",
        valueType: "array<string>",
        multiple: true,
        initial: () => ["Device A", "Device C"],
        meta: "v-model: Array<string> | init: ['Device A','Device C']",
      },
      {
        key: "stringArrayEmpty",
        label: "多选 (Array<string>) 初始化 []",
        valueType: "array<string>",
        multiple: true,
        initial: () => [],
        meta: "v-model: Array<string> | init: []",
      },
      {
        key: "stringArrayNull",
        label: "多选 (Array<string>) 初始化 null",
        valueType: "array<string>",
        multiple: true,
        initial: null,
        meta: "v-model: Array<string> | init: null",
      },
      {
        key: "stringArrayUndefined",
        label: "多选 (Array<string>) 初始化 undefined",
        valueType: "array<string>",
        multiple: true,
        initial: undefined,
        meta: "v-model: Array<string> | init: undefined",
      },
    ],
  },
  {
    key: "primitive-numbers",
    title: "Options = Array<number>",
    description: "验证 number / Array<number> 类型以及空值。",
    optionsKey: "numberPrimitives",
    optionsLabel: "Array<number>",
    targets: "number · Array<number>",
    states: [
      {
        key: "numberSingleWithValue",
        label: "单选 (number) 初始化有值",
        valueType: "number",
        multiple: false,
        initial: 1002,
        meta: "v-model: number | init: 1002",
      },
      {
        key: "numberSingleNull",
        label: "单选 (number) 初始化 null",
        valueType: "number",
        multiple: false,
        initial: null,
        meta: "v-model: number | init: null",
      },
      {
        key: "numberSingleUndefined",
        label: "单选 (number) 初始化 undefined",
        valueType: "number",
        multiple: false,
        initial: undefined,
        meta: "v-model: number | init: undefined",
      },
      {
        key: "numberArrayWithValue",
        label: "多选 (Array<number>) 有默认值",
        valueType: "array<number>",
        multiple: true,
        initial: () => [1001, 1003],
        meta: "v-model: Array<number> | init: [1001,1003]",
      },
      {
        key: "numberArrayEmpty",
        label: "多选 (Array<number>) 初始化 []",
        valueType: "array<number>",
        multiple: true,
        initial: () => [],
        meta: "v-model: Array<number> | init: []",
      },
      {
        key: "numberArrayNull",
        label: "多选 (Array<number>) 初始化 null",
        valueType: "array<number>",
        multiple: true,
        initial: null,
        meta: "v-model: Array<number> | init: null",
      },
      {
        key: "numberArrayUndefined",
        label: "多选 (Array<number>) 初始化 undefined",
        valueType: "array<number>",
        multiple: true,
        initial: undefined,
        meta: "v-model: Array<number> | init: undefined",
      },
    ],
  },
  {
    key: "primitive-booleans",
    title: "Options = Array<boolean>",
    description: "验证 boolean / Array<boolean> 以及空值。",
    optionsKey: "booleanPrimitives",
    optionsLabel: "Array<boolean>",
    targets: "boolean · Array<boolean>",
    states: [
      {
        key: "booleanSingleWithValue",
        label: "单选 (boolean) 初始化 true",
        valueType: "boolean",
        multiple: false,
        initial: true,
        meta: "v-model: boolean | init: true",
      },
      {
        key: "booleanSingleNull",
        label: "单选 (boolean) 初始化 null",
        valueType: "boolean",
        multiple: false,
        initial: null,
        meta: "v-model: boolean | init: null",
      },
      {
        key: "booleanSingleUndefined",
        label: "单选 (boolean) 初始化 undefined",
        valueType: "boolean",
        multiple: false,
        initial: undefined,
        meta: "v-model: boolean | init: undefined",
      },
      {
        key: "booleanArrayWithValue",
        label: "多选 (Array<boolean>) 有默认值",
        valueType: "array<boolean>",
        multiple: true,
        initial: () => [true],
        meta: "v-model: Array<boolean> | init: [true]",
      },
      {
        key: "booleanArrayEmpty",
        label: "多选 (Array<boolean>) 初始化 []",
        valueType: "array<boolean>",
        multiple: true,
        initial: () => [],
        meta: "v-model: Array<boolean> | init: []",
      },
      {
        key: "booleanArrayNull",
        label: "多选 (Array<boolean>) 初始化 null",
        valueType: "array<boolean>",
        multiple: true,
        initial: null,
        meta: "v-model: Array<boolean> | init: null",
      },
      {
        key: "booleanArrayUndefined",
        label: "多选 (Array<boolean>) 初始化 undefined",
        valueType: "array<boolean>",
        multiple: true,
        initial: undefined,
        meta: "v-model: Array<boolean> | init: undefined",
      },
    ],
  },
  {
    key: "object-strings",
    title: "Options = Array<{id: string, name: string}>",
    description: "验证对象数据 + string / Array<string> / Array<{id:string}> 的组合。",
    optionsKey: "stringObjects",
    optionsLabel: "Array<{id:string,name:string}>",
    targets: "string · Array<string> · Array<{id:string}>",
    valueField: "id",
    displayField: "name",
    states: [
      {
        key: "objectStringSingleWithValue",
        label: "单选 string (对象 options) 有值",
        valueType: "string",
        multiple: false,
        initial: "sensor-beta",
        meta: "options: object | v-model: string | init: 'sensor-beta'",
      },
      {
        key: "objectStringSingleNull",
        label: "单选 string (对象 options) null",
        valueType: "string",
        multiple: false,
        initial: null,
        meta: "options: object | v-model: string | init: null",
      },
      {
        key: "objectStringArrayIds",
        label: "多选 Array<string> (id 数组)",
        valueType: "array<string>",
        multiple: true,
        initial: () => ["sensor-alpha"],
        meta: "options: object | v-model: Array<string> | init: ['sensor-alpha']",
      },
      {
        key: "objectStringArrayIdsEmpty",
        label: "多选 Array<string> 空 []",
        valueType: "array<string>",
        multiple: true,
        initial: () => [],
        meta: "options: object | v-model: Array<string> | init: []",
      },
      {
        key: "objectStringArrayObjects",
        label: "多选 Array<{id:string}> 有值",
        valueType: "array<object>",
        multiple: true,
        initial: () => [
          { id: "sensor-alpha" },
          { id: "sensor-gamma" },
        ],
        meta: "options: object | v-model: Array<{id:string}> | init: 2 items",
      },
      {
        key: "objectStringArrayObjectsEmpty",
        label: "多选 Array<{id:string}> 空",
        valueType: "array<object>",
        multiple: true,
        initial: () => [],
        meta: "options: object | v-model: Array<{id:string}> | init: []",
      },
    ],
  },
  {
    key: "object-numbers",
    title: "Options = Array<{id: number, name: string}>",
    description: "验证对象数据 + number / Array<number> / Array<{id:number}> 的组合。",
    optionsKey: "numberObjects",
    optionsLabel: "Array<{id:number,name:string}>",
    targets: "number · Array<number> · Array<{id:number}>",
    valueField: "id",
    displayField: "name",
    states: [
      {
        key: "objectNumberSingleWithValue",
        label: "单选 number (对象 options) 有值",
        valueType: "number",
        multiple: false,
        initial: 22,
        meta: "options: object | v-model: number | init: 22",
      },
      {
        key: "objectNumberSingleNull",
        label: "单选 number (对象 options) null",
        valueType: "number",
        multiple: false,
        initial: null,
        meta: "options: object | v-model: number | init: null",
      },
      {
        key: "objectNumberArrayIds",
        label: "多选 Array<number> (id 数组)",
        valueType: "array<number>",
        multiple: true,
        initial: () => [11, 33],
        meta: "options: object | v-model: Array<number> | init: [11,33]",
      },
      {
        key: "objectNumberArrayIdsEmpty",
        label: "多选 Array<number> 空 []",
        valueType: "array<number>",
        multiple: true,
        initial: () => [],
        meta: "options: object | v-model: Array<number> | init: []",
      },
      {
        key: "objectNumberArrayObjects",
        label: "多选 Array<{id:number}> 有值",
        valueType: "array<object>",
        multiple: true,
        initial: () => [{ id: 22 }],
        meta: "options: object | v-model: Array<{id:number}> | init: [{id:22}]",
      },
      {
        key: "objectNumberArrayObjectsEmpty",
        label: "多选 Array<{id:number}> 空",
        valueType: "array<object>",
        multiple: true,
        initial: () => [],
        meta: "options: object | v-model: Array<{id:number}> | init: []",
      },
    ],
  },
  {
    key: "object-booleans",
    title: "Options = Array<{id: boolean, name: string}>",
    description: "验证对象数据 + boolean / Array<boolean> / Array<{id:boolean}> 的组合。",
    optionsKey: "booleanObjects",
    optionsLabel: "Array<{id:boolean,name:string}>",
    targets: "boolean · Array<boolean> · Array<{id:boolean}>",
    valueField: "id",
    displayField: "name",
    states: [
      {
        key: "objectBooleanSingleWithValue",
        label: "单选 boolean (对象 options) true",
        valueType: "boolean",
        multiple: false,
        initial: true,
        meta: "options: object | v-model: boolean | init: true",
      },
      {
        key: "objectBooleanSingleNull",
        label: "单选 boolean (对象 options) null",
        valueType: "boolean",
        multiple: false,
        initial: null,
        meta: "options: object | v-model: boolean | init: null",
      },
      {
        key: "objectBooleanArrayIds",
        label: "多选 Array<boolean> (id 数组)",
        valueType: "array<boolean>",
        multiple: true,
        initial: () => [true, false],
        meta: "options: object | v-model: Array<boolean> | init: [true,false]",
      },
      {
        key: "objectBooleanArrayIdsEmpty",
        label: "多选 Array<boolean> 空 []",
        valueType: "array<boolean>",
        multiple: true,
        initial: () => [],
        meta: "options: object | v-model: Array<boolean> | init: []",
      },
      {
        key: "objectBooleanArrayObjects",
        label: "多选 Array<{id:boolean}> 有值",
        valueType: "array<object>",
        multiple: true,
        initial: () => [{ id: false }],
        meta: "options: object | v-model: Array<{id:boolean}> | init: [{id:false}]",
      },
      {
        key: "objectBooleanArrayObjectsEmpty",
        label: "多选 Array<{id:boolean}> 空",
        valueType: "array<object>",
        multiple: true,
        initial: () => [],
        meta: "options: object | v-model: Array<{id:boolean}> | init: []",
      },
    ],
  },
];

function buildInitialValues(groups) {
  const values = {};
  groups.forEach((group) => {
    group.states.forEach((state) => {
      values[state.key] =
        typeof state.initial === "function" ? state.initial() : state.initial;
    });
  });
  return values;
}

function buildStateMap(groups) {
  const map = {};
  groups.forEach((group) => {
    group.states.forEach((state) => {
      map[state.key] = state;
    });
  });
  return map;
}

export default {
  name: "SmartSelectPage",
  components: {
    SmartSelect,
  },
  data() {
    return {
      groups: scenarioGroups,
      formValues: buildInitialValues(scenarioGroups),
      stateMap: buildStateMap(scenarioGroups),
      expandOptions: false,
    };
  },
  methods: {
    getOptions(key) {
      return optionsCatalog[key] || [];
    },
    getOptionsPreview(key) {
      return stringifyOptions(this.getOptions(key));
    },
    formatValue(value) {
      if (value === undefined) return "undefined";
      if (value === null) return "null";
      if (Array.isArray(value)) {
        return value.length ? JSON.stringify(value, null, 2) : "[]";
      }
      if (typeof value === "object") {
        const keys = Object.keys(value || {});
        return keys.length ? JSON.stringify(value, null, 2) : "{}";
      }
      if (typeof value === "string") {
        return value === "" ? '"" (empty string)' : `"${value}"`;
      }
      return String(value);
    },
    resolveInitialValue(state) {
      return typeof state.initial === "function"
        ? state.initial()
        : state.initial;
    },
    resetValue(key) {
      const state = this.stateMap[key];
      if (!state) return;
      this.$set(this.formValues, key, this.resolveInitialValue(state));
    },
    resetAll() {
      Object.keys(this.stateMap).forEach((key) => this.resetValue(key));
    },
  },
};
</script>

<style scoped>
.smart-select-test-page {
  padding: 24px;
  background: #050b17;
  color: #e2e8f0;
  min-height: 100vh;
}

.group-card {
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  background: #0f172a;
  box-shadow: 0 15px 35px -25px rgba(15, 23, 42, 0.9);
}

.group-card__header {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 20px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
}

.group-card__title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #f8fafc;
}

.group-card__desc {
  margin: 4px 0;
  font-size: 13px;
  color: #cbd5f5;
}

.group-card__meta {
  font-size: 12px;
  color: #94a3b8;
}

.group-card__tag {
  font-size: 11px;
  text-transform: uppercase;
  color: #cbd5f5;
  border: 1px solid rgba(129, 140, 248, 0.6);
  border-radius: 999px;
  padding: 4px 10px;
  align-self: flex-start;
  background: rgba(79, 70, 229, 0.25);
}

.options-preview {
  padding: 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
  font-size: 12px;
  color: #cbd5f5;
}

.options-preview summary {
  cursor: pointer;
  font-weight: 600;
  margin-bottom: 8px;
  color: #e0eefe;
}

.options-preview pre {
  max-height: 220px;
  overflow: auto;
  background: #020617;
  color: #67e8f9;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.group-grid {
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.state-card {
  border: 1px dashed rgba(79, 70, 229, 0.45);
  border-radius: 10px;
  padding: 16px;
  background: #111b2f;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.state-card__header {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.state-card__title {
  margin: 0;
  font-weight: 600;
  font-size: 14px;
  color: #f8fafc;
}

.state-card__meta {
  font-size: 11px;
  color: #94a3b8;
}

.value-preview {
  margin: 0;
  background: #020617;
  color: #cbd5f5;
  font-size: 12px;
  padding: 8px;
  border-radius: 6px;
  min-height: 54px;
  overflow: auto;
  border: 1px solid rgba(148, 163, 184, 0.2);
}

@media (max-width: 640px) {
  .smart-select-test-page {
    padding: 16px;
  }
}
</style>
